import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { PricedQuoteCardSkeleton } from './PricedQuoteCardSkeleton'
import { QUOTE_CARD_WIDTH } from './quoteCardTypes'

// Enough to fill the carousel on desktop. The real count isn't known until the application lands.
const LOADING_CARD_COUNT = 3
// Drivers, trucks, trailers and claims history, then documents and additional info.
const FLEET_CARD_WIDTHS = ['w-40', 'w-40', 'w-40', 'w-48']
const DETAIL_CARD_WIDTHS = ['w-44', 'w-44']
// Coverages, rating, financing, claims capabilities and COI speed.
const FILTER_WIDTHS = ['w-28', 'w-24', 'w-40', 'w-40', 'w-28']

/** The quotes page while it loads, laid out like it: summary, filters, heading, then a row of quote cards. */
export function QuotesPageSkeleton() {
  return (
    <SkeletonGroup data-page-loading aria-label="Loading quotes" className="pb-20">
      {/* Mobile collapses the summary behind its title row. */}
      <div className="mb-2 flex items-center justify-between md:hidden">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="size-8" />
      </div>
      <div className="mb-2 flex gap-1 max-md:hidden">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-8 w-36" />
      </div>
      {/* Cards grow to share the row, split by a rule, as the real ones do. */}
      <div className="flex flex-wrap items-stretch gap-3 max-md:hidden">
        {FLEET_CARD_WIDTHS.map((width, index) => (
          <Skeleton key={index} className={cn('h-[76px] grow rounded-xl', width)} />
        ))}
        <div className="bg-ink-100 w-px self-stretch" aria-hidden />
        {DETAIL_CARD_WIDTHS.map((width, index) => (
          <Skeleton key={index} className={cn('h-[76px] grow rounded-xl', width)} />
        ))}
      </div>
      <div className="border-ink-100 mt-4 border-t" />

      {/* mt-4 is the filters' progress-bar slot; the sort tabs fill the spare width as they do there. */}
      <div className="mt-4 mb-8 flex gap-3 overflow-hidden">
        <Skeleton className="h-8 w-52 shrink-0 rounded-xl md:w-auto md:min-w-52 md:flex-1" />
        {FILTER_WIDTHS.map((width, index) => (
          <Skeleton key={index} className={cn('h-8 shrink-0 rounded-xl', width)} />
        ))}
      </div>

      <div className="mb-8 flex items-center justify-between gap-3">
        <Skeleton className="h-9 w-32 md:w-56" />
        <div className="flex gap-2">
          <Skeleton className="size-10 rounded-full" />
          <Skeleton className="size-10 rounded-full" />
        </div>
      </div>

      {/* The carousel's own track: same card width, gap and mobile bleed, so the cards land where these were. */}
      <div className="flex gap-4 overflow-hidden pt-2 pb-4 max-md:-mx-4 max-md:pl-4">
        {Array.from({ length: LOADING_CARD_COUNT }).map((_, index) => (
          <div key={index} aria-hidden className={cn(QUOTE_CARD_WIDTH, 'shrink-0')}>
            <PricedQuoteCardSkeleton />
          </div>
        ))}
      </div>
    </SkeletonGroup>
  )
}
